import type {
  NameType,
  Payload,
  ValueType,
} from 'recharts/types/component/DefaultTooltipContent';

import { type LineConfig, type RegionConfig } from '@/components/charts/types';
import i18n from '@/i18n/config';
import { formatDateForDisplay, isWeekend } from '@/lib/utils';
import type { Simulation } from '@/routes/(app)/(scenarios)/types';

export interface OccupancyChartRow {
  date: string;
  regular: Record<string, number>;
  reefer: Record<string, number>;
}

type TooltipPayload = Payload<ValueType, NameType>;
type NumericTooltipPayload = TooltipPayload & { value: number };

export interface OccupancyStats {
  min: NumericTooltipPayload;
  median: NumericTooltipPayload | null;
  max: NumericTooltipPayload | null;
}

/**
 * Derives min, median, and max from a recharts tooltip payload.
 * Returns null when there are no numeric entries to compare.
 */
export function deriveOccupancyStats(
  payload: Array<TooltipPayload>
): OccupancyStats | null {
  const numeric = payload.filter(
    (item): item is NumericTooltipPayload =>
      item.type !== 'none' && typeof item.value === 'number'
  );

  if (!numeric.length) return null;

  const sorted = numeric.toSorted((left, right) => left.value - right.value);
  const lastIndex = sorted.length - 1;
  const min = sorted.at(0);
  if (min === undefined) return null;

  const median =
    sorted.length >= 3 ? (sorted.at(Math.floor(lastIndex / 2)) ?? null) : null;
  const max = sorted.length >= 2 ? (sorted.at(lastIndex) ?? null) : null;

  return { min, median, max };
}

/**
 * Map every character to a 32-bit integer hash (FNV-1a style mix).
 */
function hashString(input: string): number {
  let hash = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return Math.abs(hash);
}

interface ColorForIdProps {
  id: string;
  mutedColors?: boolean;
}
/**
 * Deterministic HSL for an id
 */
export function colorForId({ id, mutedColors }: ColorForIdProps): string {
  const hash = hashString(id);
  const hue = hash % 360;

  const configurations = {
    muted: {
      saturation: 26 + (hash % 10),
      lightness: 62 + (hash % 11),
    },
    vibrant: {
      saturation: 50 + (hash % 30),
      lightness: 52 + (hash % 9),
    },
  };

  return buildHslColor({
    hue,
    ...(mutedColors ? configurations.muted : configurations.vibrant),
  });
}

interface BuildHslColorProps {
  hue: number;
  saturation: number;
  lightness: number;
}
function buildHslColor({
  hue,
  saturation,
  lightness,
}: BuildHslColorProps): string {
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
}

export interface BuildSimulationLinesOptions {
  mutedColors?: boolean;
  showSeriesNames?: boolean;
  showValueLabels?: boolean;
}

interface BuildSimulationLineProps {
  simulations: Array<Simulation>;
  options: BuildSimulationLinesOptions;
}
export function buildSimulationLines(
  props: BuildSimulationLineProps
): Array<LineConfig> {
  const { simulations, options } = props;
  const { mutedColors, showSeriesNames, showValueLabels } = options;

  return simulations.map((simulation, index) => ({
    key: simulation.simulation_run_id,
    label: showSeriesNames
      ? i18n.t('simulations.table.runNumber', { number: index + 1 })
      : '',
    color: colorForId({ id: simulation.simulation_run_id, mutedColors }),
    curveType: 'linear',
    strokeWidth: 2,
    strokeOpacity: mutedColors ? 0.45 : 1,
    dot: showValueLabels,
    showLabelList: showValueLabels,
  }));
}

export const MEDIAN_LINE_KEY = 'median';

function computeMedianValue(values: Array<number>): number {
  const sorted = [...values].toSorted((left, right) => left - right);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? Math.round(((sorted.at(mid - 1) ?? 0) + (sorted.at(mid) ?? 0)) / 2)
    : (sorted.at(mid) ?? 0);
}

export function buildMedianLine(): LineConfig {
  return {
    key: MEDIAN_LINE_KEY,
    label: 'Median',
    color: '#000000',
    strokeWidth: 2,
    curveType: 'linear',
    dot: true,
    showLabelList: true,
  };
}

export function addMedianToRows(
  rows: Array<OccupancyChartRow>
): Array<OccupancyChartRow> {
  return rows.map((row) => ({
    ...row,
    regular: {
      ...row.regular,
      [MEDIAN_LINE_KEY]: computeMedianValue(Object.values(row.regular)),
    },
    reefer: {
      ...row.reefer,
      [MEDIAN_LINE_KEY]: computeMedianValue(Object.values(row.reefer)),
    },
  }));
}

export function pivotSimulationsByDate(
  simulations: Array<Simulation>
): Array<OccupancyChartRow> {
  const byDate = new Map<
    string,
    {
      date: string;
      regular: Record<string, number>;
      reefer: Record<string, number>;
    }
  >();

  for (const simulation of simulations) {
    const simulationRunId = simulation.simulation_run_id;
    for (const shift of simulation.data) {
      const dateKey = shift.shift_datetime;
      const existing = byDate.get(dateKey);
      if (existing) {
        existing.regular[simulationRunId] = shift.regular;
        existing.reefer[simulationRunId] = shift.reefer;
        continue;
      }
      byDate.set(dateKey, {
        date: formatDateForDisplay(dateKey),
        regular: { [simulationRunId]: shift.regular },
        reefer: { [simulationRunId]: shift.reefer },
      });
    }
  }

  return Array.from(byDate.values()).toSorted(
    (left, right) =>
      new Date(left.date).getTime() - new Date(right.date).getTime()
  );
}

export function buildWeekendRegions(
  rows: Array<{ date: string }>
): Array<RegionConfig> {
  const ranges: Array<RegionConfig> = [];
  let start: string | null = null;
  let last: string | null = null;

  for (const row of rows) {
    if (isWeekend(row.date)) {
      start ??= row.date;
      last = row.date;
      continue;
    }
    if (start !== null && last !== null) {
      ranges.push({ x1: start, x2: last, fillOpacity: 0.15 });
    }
    start = null;
    last = null;
  }

  if (start !== null && last !== null) {
    ranges.push({ x1: start, x2: last, fillOpacity: 0.15 });
  }

  return ranges;
}

export interface ScenarioCardStats {
  maxRegularOccupancy: number;
  maxReeferOccupancy: number;
  amountOfSimulations: number;
}

/**
 * Derives peak occupancy percentages and simulation count from all simulations.
 */
export function deriveScenarioCardStats(
  simulations: Array<Simulation>
): ScenarioCardStats {
  let maxRegularOccupancy = 0;
  let maxReeferOccupancy = 0;

  for (const simulation of simulations) {
    for (const shift of simulation.data) {
      maxRegularOccupancy = Math.max(maxRegularOccupancy, shift.regular);
      maxReeferOccupancy = Math.max(maxReeferOccupancy, shift.reefer);
    }
  }

  return {
    maxRegularOccupancy,
    maxReeferOccupancy,
    amountOfSimulations: simulations.length,
  };
}
